<script setup lang="ts">
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@zerohuman/ui'
import { computed } from 'vue'
import { API_TOKEN_RESOURCES, CONNECTION_ONLY_RESOURCES, PERSON_ONLY_SCOPES } from '@/api'
import type { ScopeLevel } from '@/composables/useScopeLevels'

/**
 * One choice that sets every resource in a `ScopeLevelPicker` at once, sat beside the heading of the panel the
 * picker is in. It edits the same levels the picker does, so the two can never disagree: it shows the level every
 * resource shares, and reads "Mixed" once one of them is set on its own.
 */
const props = defineProps<{
    testid: string
    /** What each level says in the list, where a screen words it its own way. The level's own name otherwise. */
    levelLabels?: Partial<Record<ScopeLevel, string>>
    /** Granting an API token or a connected app, not a person: `write` stops short of scopes only a person may hold. */
    credential?: boolean
    /** Approving a connected app (#883): the person's own resources (`mail`) are set with the rest. Left out otherwise. */
    connection?: boolean
}>()
const levels = defineModel<Record<string, ScopeLevel>>({ required: true })

/** Narrowest first, the order the picker offers them in. */
const LEVELS: readonly ScopeLevel[] = ['none', 'read', 'write']

/** The resources the picker beside this control offers, which are the ones this control sets and reads. */
const resources = computed<readonly string[]>(() =>
    props.connection
        ? API_TOKEN_RESOURCES
        : API_TOKEN_RESOURCES.filter(resource => !CONNECTION_ONLY_RESOURCES.includes(resource)),
)

/** What `level` comes to for `resource`: `write` is `read` where a credential may not write (billing:write). */
function levelFor(resource: string, level: ScopeLevel): ScopeLevel {
    return props.credential && level === 'write' && PERSON_ONLY_SCOPES.includes(`${resource}:write`) ? 'read' : level
}

/** The level every offered resource is at. None when the levels are mixed. */
const shared = computed(() =>
    LEVELS.find(level => resources.value.every(resource => levels.value[resource] === levelFor(resource, level))),
)

/** Every offered resource at `level`; a resource this screen does not offer stays at `none`. */
function setAll(level: ScopeLevel): void {
    levels.value = {
        ...Object.fromEntries(API_TOKEN_RESOURCES.map(r => [r, 'none' as ScopeLevel])),
        ...Object.fromEntries(resources.value.map(r => [r, levelFor(r, level)])),
    }
}
</script>

<template>
	<div class="flex items-center gap-2 text-sm">
		<span class="text-muted-foreground text-xs">All</span>
		<Select :model-value="shared" :data-testid="testid" @update:model-value="setAll($event as ScopeLevel)">
			<SelectTrigger size="sm" aria-label="Level for every resource">
				<SelectValue placeholder="Mixed" />
			</SelectTrigger>
			<SelectContent>
				<SelectItem v-for="level in LEVELS" :key="level" :value="level">{{ levelLabels?.[level] ?? level }}</SelectItem>
			</SelectContent>
		</Select>
	</div>
</template>
